import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import type { TokenFilterInput } from "@/lib/tokenFilter";
import { filterTokens } from "@/lib/tokenFilter";
import { normalizeAddress } from "@/lib/utils/address";
import { ERC20_TRANSFER_SIG, padAddressToTopic } from "../evmUtils";
import type { FallbackDataFetcher, FallbackFetchContext } from "../types";

interface WalletActivity {
  chainId: number;
  transactionHash: string;
  blockNumber: number;
  timestamp: string;
  type: string; // "receive", "send", "swap", etc.
  assetType: string | null; // "native", "erc20", "erc721", "erc1155"
  from: string | null;
  to: string | null;
  tokenAddress: string | null;
  value: string;
  tokenMetadata?: {
    decimals: number;
    symbol: string;
    name: string;
    logo?: string;
  };
  transfers: Array<{
    tokenAddress: string | null;
    symbol: string;
    amount: string;
    decimals: number;
    from: string;
    to: string | null;
    valueUsd: number | null;
  }>;
}

interface ActivityResponse {
  activities: WalletActivity[];
  hasMore: boolean;
}

function resolveTokenAddress(a: WalletActivity): string | null {
  return a.tokenAddress ?? a.transfers?.[0]?.tokenAddress ?? null;
}

/**
 * Fetch recent token transfers via sim-dune-get-wallet-activity action proxy.
 * Used as a fallback for erc20-receive, erc20-send, eth-receive, eth-send triggers
 * when standard live test and last-run data return nothing.
 */
export const evmTransferFallback: FallbackDataFetcher = async (ctx: FallbackFetchContext) => {
  const { triggerType, triggerPayload, wallets } = ctx;

  // Native ETH triggers use `walletAddress`; ERC-20 triggers use `fromAddress`/`toAddress`.
  const isReceive = triggerType === "erc20-receive" || triggerType === "eth-receive";
  const isNativeTrigger = triggerType === "eth-send" || triggerType === "eth-receive";
  const monitoredAddress = isReceive
    ? ((triggerPayload.toAddress as string | undefined) ?? (triggerPayload.walletAddress as string | undefined))
    : ((triggerPayload.fromAddress as string | undefined) ??
      (triggerPayload.walletAddress as string | undefined) ??
      (triggerPayload.toAddress as string | undefined));

  if (!monitoredAddress) return [];

  const matchingWallet = wallets.find(w => w.address.toLowerCase() === monitoredAddress.toLowerCase());
  if (!matchingWallet) return [];

  const chainId = triggerPayload.chainId as number | undefined;
  const payload: Record<string, unknown> = {
    address: monitoredAddress,
    limit: 5,
    filterSpamTokens: true,
  };
  if (chainId) payload.chainIds = [chainId];

  const response = await authenticatedFetch<{ code: number; data: { result: ActivityResponse; success: boolean } }>(
    `${V2_ENDPOINT}/v1/action-proxy/sim-dune-get-wallet-activity/query`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ payload, walletId: matchingWallet.id }),
    },
  );

  if (!response.ok) return [];
  const data = await response.json();
  if (data.code !== 0 || !data.data?.success) return [];

  const activities = data.data.result?.activities ?? [];

  const expectedType = isReceive ? "receive" : "send";
  const tokenAddress = triggerPayload.tokenAddress as string | undefined;

  let filtered = activities.filter((a: WalletActivity) => {
    if (a.type !== expectedType) return false;
    if (isNativeTrigger && a.assetType !== "native") return false;
    if (!isNativeTrigger && a.assetType === "native") return false;
    if (tokenAddress) {
      const activityToken = resolveTokenAddress(a);
      if (!activityToken || normalizeAddress(activityToken) !== normalizeAddress(tokenAddress)) return false;
    }
    return true;
  });

  if (!isNativeTrigger && filtered.length > 0) {
    const uniqueTokens = new Map<string, TokenFilterInput>();
    for (const a of filtered) {
      const token = resolveTokenAddress(a);
      if (token) {
        const key = `${a.chainId}:${normalizeAddress(token)}`;
        if (!uniqueTokens.has(key)) {
          uniqueTokens.set(key, {
            chainId: a.chainId,
            address: token,
            symbol: a.transfers?.[0]?.symbol ?? a.tokenMetadata?.symbol,
          });
        }
      }
    }

    if (uniqueTokens.size > 0) {
      const filterResult = await filterTokens([...uniqueTokens.values()]);
      const passedKeys = new Set(filterResult.tokens.map(t => `${t.chainId}:${normalizeAddress(t.address)}`));
      filtered = filtered.filter((a: WalletActivity) => {
        const token = resolveTokenAddress(a);
        if (!token) return true;
        return passedKeys.has(`${a.chainId}:${normalizeAddress(token)}`);
      });
    }
  }

  return filtered.map((a: WalletActivity) => {
    const transfer = a.transfers?.[0];
    const sender = a.from ?? transfer?.from ?? "";
    const recipient = a.to ?? transfer?.to ?? monitoredAddress;
    const amount = transfer?.amount ?? a.value ?? "0";
    const symbol = transfer?.symbol ?? a.tokenMetadata?.symbol;
    const decimals = transfer?.decimals ?? a.tokenMetadata?.decimals;
    const token = resolveTokenAddress(a) ?? "";

    // Native ETH triggers use a flat result schema (txHash, chainId, from, to, value, blockNumber).
    // ERC-20 triggers use a nested schema (params: {from, to, value}, raw: {address, ...}).
    if (isNativeTrigger) {
      return {
        triggeredAt: a.timestamp,
        chainId: a.chainId,
        txHash: a.transactionHash,
        from: sender,
        to: recipient,
        value: amount,
        blockNumber: a.blockNumber,
        _symbol: symbol,
        _decimals: decimals,
      };
    }

    const result: Record<string, unknown> = {
      params: {
        from: sender,
        to: recipient,
        value: amount,
      },
      raw: {
        address: token,
        blockNumber: a.blockNumber,
        transactionHash: a.transactionHash,
        logIndex: 0,
        topics: [ERC20_TRANSFER_SIG, padAddressToTopic(sender), padAddressToTopic(recipient)],
      },
      triggeredAt: a.timestamp,
    };
    if (symbol) result._symbol = symbol;
    if (decimals != null) result._decimals = decimals;
    return result;
  });
};
